/** @jsxImportSource @emotion/react */
import { useSession } from '@quickbit/backoffice-core-frontend';
import {
  Input,
  List,
  ListItem,
  Typography,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useAsyncFn } from 'react-use';
import {
  restartProcessingForOrder,
  retryProcessingPay,
  retryFinalInternalConfirmation,
  retrySellCryptoForOrder,
  resendCallback,
  retryMoveToCryptoReceived,
  retryRefundForPayOrder,
  forceCancelOrder,
} from '../api';
import { OrderMinimal } from '../types';

interface Props {
  orders: OrderMinimal[];
}
export const RetryActions = ({ orders }: Props) => {
  const session = useSession();
  const [payTransactionId, setPayTransactionId] = useState<string>();

  const canEditOrders = useMemo(() => {
    return (
      session.authenticated &&
      session.permissions.includes('edit_merchant_orders')
    );
  }, [session]);

  const [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { loading: forceCancelLoading, value: forceCancelValue },
    forceCancel,
  ] = useAsyncFn(async () => {
    const results = await Promise.all(
      orders.map(async (order) => {
        try {
          await forceCancelOrder(order.uuid);
          return { [order.uuid]: true };
        } catch {
          return { [order.uuid]: false };
        }
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.assign({}, ...results);
  }, [orders]);

  const [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { loading: retryPayProcessingLoading, value: retryPayProcessingValue },
    retryPayProcessing,
  ] = useAsyncFn(async () => {
    const results = await Promise.all(
      orders.map(async (order) => {
        try {
          const success = await retryProcessingPay(order.uuid);
          return { [order.uuid]: success };
        } catch {
          return { [order.uuid]: false };
        }
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.assign({}, ...results);
  }, [orders]);

  const [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { loading: retrySellCryptoLoading, value: retrySellCryptoValue },
    retrySellCrypto,
  ] = useAsyncFn(async () => {
    const results = await Promise.all(
      orders.map(async (order) => {
        try {
          const success = await retrySellCryptoForOrder(order.uuid);
          return { [order.uuid]: success };
        } catch {
          return { [order.uuid]: false };
        }
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.assign({}, ...results);
  }, [orders]);

  const [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { loading: moveToCryptoReceivedLoading, value: moveToCryptoReceivedValue },
    moveToCryptoReceived,
  ] = useAsyncFn(async () => {
    const results = await Promise.all(
      orders.map(async (order) => {
        try {
          const success = await retryMoveToCryptoReceived(order.uuid);
          return { [order.uuid]: success };
        } catch {
          return { [order.uuid]: false };
        }
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.assign({}, ...results);
  }, [orders]);

  const [
    {
      loading: retryFinalInternalConfLoading,
      value: retryFinalInternalConfValue,
    },
    retryFinalInternalConf,
  ] = useAsyncFn(async () => {
    // Retry with input can't be batched
    if (orders.length !== 1) return;
    const order = orders[0];
    if (order.receiveOrderPreviewId && payTransactionId) {
      try {
        const success = await retryFinalInternalConfirmation({
          orderId: order.uuid,
          receiveOrderPreviewId: order.receiveOrderPreviewId,
          payTransactionId,
        });
        return { [order.uuid]: success };
      } catch {
        return { [order.uuid]: false };
      }
    }
  }, [orders, payTransactionId]);

  const [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { loading: restartProcessingLoading, value: restartProcessingValue },
    restartProcessing,
  ] = useAsyncFn(async () => {
    const results = await Promise.all(
      orders.map(async (order) => {
        try {
          const success = await restartProcessingForOrder(order.uuid);
          return { [order.uuid]: success };
        } catch {
          return { [order.uuid]: false };
        }
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.assign({}, ...results);
  }, [orders]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [{ value: resendValue, loading: resendLoading }, resend] = useAsyncFn(
    async () => {
      const results = await Promise.all(
        orders.map(async (order) => {
          try {
            await resendCallback(order.uuid);
            return { [order.uuid]: true };
          } catch {
            return { [order.uuid]: false };
          }
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return Object.assign({}, ...results);
    }
  );

  const [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    { loading: retryRefundLoading, value: retryRefundValue },
    retryRefund,
  ] = useAsyncFn(async () => {
    const results = await Promise.all(
      orders.map(async (order) => {
        try {
          const success = await retryRefundForPayOrder(order.uuid);
          return { [order.uuid]: success };
        } catch {
          return { [order.uuid]: false };
        }
      })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.assign({}, ...results);
  }, [orders]);

  const everyOrderInStates = (...states: string[]) => {
    return orders.every((order) => states.includes(order.status));
  };

  const anyOrderInStates = (...states: string[]) => {
    return orders.some((order) => states.includes(order.status));
  };

  const everyOrderIsProcessing = orders.every(
    (order) => order.useProcessingFlow
  );

  const displayResult = (result: Record<string, boolean> | undefined) => {
    if (!result) return '';
    if (Object.keys(result).length === 1) {
      return Object.values(result)[0] ? 'Success' : 'Failed';
    }
    return (
      <>
        {Object.entries(result).map(([orderId, success]) => (
          <div key={orderId}>
            <Typography variant="bodySmallest">
              {orderId}: {success ? 'Success' : 'Failed'}
            </Typography>
          </div>
        ))}
      </>
    );
  };

  if (!canEditOrders) return null;

  return (
    <>
      <Typography variant="bodyBold">Retry actions</Typography>
      <List>
        {everyOrderInStates('CRYPTO_RECEIVED') && (
          <ListItem
            icon={
              !retrySellCryptoValue ? (
                <UtilityButton
                  text={'Retry selling crypto for order'}
                  loading={retrySellCryptoLoading}
                  disabled={retrySellCryptoLoading}
                  onClick={retrySellCrypto}
                />
              ) : undefined
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            title={displayResult(retrySellCryptoValue)}
          />
        )}
        {everyOrderInStates('COMPLETE') && (
          <ListItem
            icon={
              moveToCryptoReceivedValue === undefined ? (
                <UtilityButton
                  text={'Retry move to CRYPTO_RECEIVED'}
                  loading={moveToCryptoReceivedLoading}
                  disabled={moveToCryptoReceivedLoading}
                  onClick={moveToCryptoReceived}
                />
              ) : undefined
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            title={displayResult(moveToCryptoReceivedValue)}
          />
        )}
        {everyOrderInStates('REFUND_CRYPTO_RECEIVED') && (
          <ListItem
            icon={
              retryRefundValue === undefined ? (
                <UtilityButton
                  text={'Retry refund'}
                  loading={retryRefundLoading}
                  disabled={retryRefundLoading}
                  onClick={retryRefund}
                />
              ) : undefined
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            title={displayResult(retryRefundValue)}
          />
        )}
        {everyOrderIsProcessing &&
          everyOrderInStates('PROCESSING_INTERNAL', 'BROKER_SENT') && (
            <ListItem
              icon={
                !retryPayProcessingValue ? (
                  <UtilityButton
                    text={'Retry paying for order'}
                    loading={retryPayProcessingLoading}
                    disabled={retryPayProcessingLoading}
                    onClick={retryPayProcessing}
                  />
                ) : undefined
              }
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              title={displayResult(retryPayProcessingValue)}
            />
          )}
        {everyOrderInStates('PROCESSING_INTERNAL') && (
          <ListItem
            icon={
              !retryFinalInternalConfValue ? (
                <UtilityButton
                  text={'Retry Final internal conf'}
                  loading={retryFinalInternalConfLoading}
                  disabled={retryFinalInternalConfLoading || !payTransactionId}
                  onClick={retryFinalInternalConf}
                />
              ) : undefined
            }
            title={displayResult(retryFinalInternalConfValue)}
            subcontent={
              <Input
                placeholder="Pay transaction id"
                value={payTransactionId ?? ''}
                onChange={(e) => setPayTransactionId(e.target.value)}
              />
            }
          />
        )}
        {everyOrderIsProcessing && everyOrderInStates('ADDRESS_CREATED') && (
          <ListItem
            icon={
              !restartProcessingValue ? (
                <UtilityButton
                  text={'Restart processing'}
                  loading={restartProcessingLoading}
                  disabled={restartProcessingLoading}
                  onClick={restartProcessing}
                />
              ) : undefined
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            title={displayResult(restartProcessingValue)}
          />
        )}
        {!anyOrderInStates('CANCELLED') && (
          <ListItem
            icon={
              !forceCancelValue ? (
                <UtilityButton
                  text={'Force Cancel'}
                  loading={forceCancelLoading}
                  disabled={forceCancelLoading}
                  onClick={forceCancel}
                />
              ) : undefined
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            title={displayResult(forceCancelValue)}
          />
        )}
        <ListItem
          icon={
            !resendValue ? (
              <UtilityButton
                text={'Resend callback'}
                loading={resendLoading}
                disabled={resendLoading}
                onClick={resend}
              />
            ) : undefined
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          title={displayResult(resendValue)}
        />
      </List>
    </>
  );
};
